import DOMPurify from 'dompurify';

/**
 * Sanitizing inbound email HTML before it is rendered.
 *
 * Eight `dangerouslySetInnerHTML` sinks rendered email bodies raw, and the app had
 * no sanitizer dependency at all. These are *inbound* emails — content an attacker
 * sends in rather than content anyone here authored — so a borrower could email
 * crafted HTML into a collections case and have it execute when an agent opened
 * the timeline. Chained with the session token living in `localStorage`, that is a
 * path from "send an email" to "hold an Admin-capable token".
 *
 * Two things worth knowing about the shape of this fix:
 *
 * **It is an allow-list, not a block-list.** Tags and attributes not named here
 * are removed. A block-list has to anticipate every vector; an allow-list only has
 * to name what a formatted email legitimately needs.
 *
 * **It is a client-side control only.** The raw HTML is still stored server-side
 * exactly as received, unchanged by this. Anything that renders those records
 * outside this app — an export, a PDF, a future admin tool — is not covered by
 * this and needs its own sanitization at its own boundary.
 *
 * The session-token half of the chain (the token in `localStorage`) is a separate
 * piece of work and is still open; until it lands the chain is half-closed, not
 * closed.
 */

/**
 * Tags a formatted email legitimately uses. Everything else is stripped.
 *
 * `#text` is listed explicitly and is load-bearing: supplying `ALLOWED_TAGS`
 * *replaces* DOMPurify's default list rather than adding to it, and the default
 * includes `#text`. Without it every text node is dropped — the markup survives
 * and the words do not, so each email renders as a shape with nothing in it.
 */
const ALLOWED_TAGS = [
  '#text',
  'a', 'b', 'blockquote', 'br', 'caption', 'code', 'col', 'colgroup', 'dd', 'div',
  'dl', 'dt', 'em', 'figcaption', 'figure', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'hr', 'i', 'img', 'li', 'ol', 'p', 'pre', 'q', 's', 'small', 'span', 'strike',
  'strong', 'sub', 'sup', 'table', 'tbody', 'td', 'tfoot', 'th', 'thead', 'tr',
  'u', 'ul',
];

/**
 * Attributes that survive. `style` is included because real emails rely on inline
 * styles for their entire layout, and stripping it turns every message into
 * unreadable soup. DOMPurify parses and rebuilds the style attribute rather than
 * passing it through, so `expression()` and `url(javascript:…)` do not survive.
 */
const ALLOWED_ATTR = [
  'align', 'alt', 'bgcolor', 'border', 'cellpadding', 'cellspacing', 'class',
  'colspan', 'dir', 'height', 'href', 'rowspan', 'span', 'src', 'style', 'title',
  'valign', 'width',
];

/**
 * URI schemes allowed on `href` and `src`.
 *
 * `javascript:` is absent, which is the point. `data:` is absent too: a `data:`
 * URL on an `img` is how a tracking pixel or an SVG-borne script arrives, and
 * legitimate email images are either `cid:` (already stripped by the mail
 * pipeline) or `https:`.
 */
const ALLOWED_URI_REGEXP = /^(?:https?|mailto|tel):/i;

/**
 * Presentational attributes that hold no URI and must be treated as inert.
 *
 * This is subtle and worth spelling out. DOMPurify allows an attribute if it is
 * *inert* (its default inert list is `alt`, `class`, `style`, `title`, `id`, …) or
 * if its value passes `ALLOWED_URI_REGEXP`. Tightening that regexp to require a
 * scheme — which is the whole point of it, for `href` and `src` — therefore also
 * makes `cellpadding="4"` fail, because `4` is not a URI. The result is a
 * sanitizer that quietly strips every table's layout while looking correct.
 *
 * Naming them here marks them inert, so the strict URI rule applies only to the
 * attributes that actually carry URIs.
 */
const URI_SAFE_PRESENTATION_ATTR = [
  'align', 'bgcolor', 'border', 'cellpadding', 'cellspacing', 'colspan', 'dir',
  'height', 'rowspan', 'span', 'valign', 'width',
];

const PURIFY_CONFIG = {
  ALLOWED_TAGS,
  ALLOWED_ATTR,
  ALLOWED_URI_REGEXP,
  ADD_URI_SAFE_ATTR: URI_SAFE_PRESENTATION_ATTR,
  // Comments can carry conditional-comment payloads that some parsers execute.
  ALLOW_ARIA_ATTR: false,
  ALLOW_DATA_ATTR: false,
  // Whole subtrees to drop rather than unwrap: unwrapping <script> would leave
  // its text content behind as visible page content.
  FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input',
    'button', 'textarea', 'select', 'link', 'meta', 'base', 'svg', 'math'],
  FORBID_ATTR: ['srcset', 'formaction', 'xlink:href', 'action', 'background',
    'poster', 'ping'],
  KEEP_CONTENT: false,
  RETURN_TRUSTED_TYPE: false,
};

/**
 * Force every surviving link to open safely.
 *
 * `target="_blank"` without `rel="noopener"` hands the opened page a reference
 * back to this one via `window.opener`. Applied as a hook so it covers links the
 * allow-list admits, rather than relying on each render site to remember.
 */
let hookInstalled = false;
function installLinkHook() {
  if (hookInstalled || typeof DOMPurify.addHook !== 'function') return;
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A' && node.getAttribute('href')) {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer nofollow');
    }
  });
  hookInstalled = true;
}

/**
 * Sanitize an inbound email body for rendering.
 *
 * Returns a string, always — never null, never undefined, never a thrown error.
 * A sanitizer that throws on malformed input would blank the page it was meant to
 * protect, and truncated or malformed HTML is normal in a mail pipeline rather
 * than exceptional. On failure it returns the input with all markup escaped, so
 * the agent sees the raw text rather than nothing at all.
 *
 * @param {unknown} html Untrusted HTML, or anything at all.
 * @returns {string} HTML safe to pass to `dangerouslySetInnerHTML`.
 */
export function sanitizeEmailHtml(html) {
  if (html === null || html === undefined) return '';
  const input = typeof html === 'string' ? html : String(html);
  if (!input.trim()) return '';

  try {
    installLinkHook();
    return DOMPurify.sanitize(input, PURIFY_CONFIG);
  } catch (error) {
    // Escaped rather than dropped: the body is often the only record of what a
    // borrower said, so showing it as text beats showing nothing.
    console.error('Email sanitization failed; falling back to escaped text', error);
    return escapeHtml(input);
  }
}

/** Escape every markup character. The fallback when sanitization itself fails. */
export function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * The props object for a sanitized render site.
 *
 * Sinks use `dangerouslySetInnerHTML={sanitizedHtmlProps(body)}` rather than
 * building the object themselves, so "is this sink sanitized?" is answerable by
 * looking at one line, and the repository-wide check that no raw sink exists has
 * a single pattern to look for.
 *
 * @param {unknown} html Untrusted HTML.
 * @returns {{__html: string}}
 */
export function sanitizedHtmlProps(html) {
  return { __html: sanitizeEmailHtml(html) };
}

export const SANITIZER_CONFIG_FOR_TESTS = PURIFY_CONFIG;
